// ModernPricingPage.js
import React, {
  useRef,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import { RippleButton } from "./RippleButton";
import { useNavigate } from "react-router-dom";
import { toPersianDigits } from "../../../helper/toPersianDigits";
import { API_BASE_URL } from "../../../config/api";
import Loader from "../../../Components/Loader/Button";

// --- Internal Helper Components (Not exported) --- //

const CheckIcon = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

// --- EXPORTED Building Blocks --- //

export const PricingCard = ({
  id,
  planName,
  description,
  price,
  discountPrice,
  discountPercent,
  features,
  buttonText,
  isPopular = false,
  buttonVariant = "primary",
}) => {
  const navigate = useNavigate();
  const [isPurchasing, setIsPurchasing] = useState(false);
  const purchaseInFlightRef = useRef(false);

  const handlePurchaseClick = async () => {
    if (purchaseInFlightRef.current) {
      return;
    }

    purchaseInFlightRef.current = true;
    setIsPurchasing(true);

    try {
      let response = await fetch(
        `${API_BASE_URL}payment-payout/initialize/${id}/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access")}`,
          },
        },
      );

      // If access token is invalid/expired → try refresh
      if (response.status === 401) {
        const refreshResponse = await fetch(
          `${API_BASE_URL}auth/trader/jwt/refresh/`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              refresh: localStorage.getItem("refresh"),
            }),
          },
        );

        if (!refreshResponse.ok) {
          // refresh failed → logout
          localStorage.removeItem("access");
          localStorage.removeItem("authCredentials");
          localStorage.removeItem("refresh");
          localStorage.removeItem("role");
          localStorage.removeItem("email");
          navigate("/signin");
          return; // ⬅️ IMPORTANT
        }

        const refreshData = await refreshResponse.json();
        localStorage.setItem("access", refreshData.access);

        // retry original request with new access token
        response = await fetch(
          `${API_BASE_URL}payment-payout/initialize/${id}/`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("access")}`,
            },
          },
        );
      }

      // Now handle final response (either first or retried)
      if (!response.ok) {
        throw new Error(`https error! Status: ${response.status}`);
      }

      const data = await response.json();
      console.log("Account Data:", data);
      navigate(`/purchase/confirm?plan=${encodeURIComponent(planName)}`, {
        state: { planName },
      });
    } catch (error) {
      console.error("Error fetching account data:", error);
    } finally {
      purchaseInFlightRef.current = false;
      setIsPurchasing(false);
    }
  };
  const hasDiscount = Boolean(discountPrice);
  const discountLabel = discountPercent
    ? `${toPersianDigits(discountPercent)}٪ تخفیف`
    : "تخفیف";
  const cardClasses = `
    max-w-[20rem] md:max-w-2xl backdrop-blur-[14px] mx-auto rounded-2xl flex-1 min-w-fit p-3 sm:p-5 flex flex-col md:flex-row transition-all duration-300
    bg-zi-bg2-light dark:bg-zi-bg2-dark border border-transparent
    ${isPopular || hasDiscount ? "relative " : ""}
  `;

  const buttonClasses = `
     w-full py-1 rounded-xl font-medium text-base transition bg-zi-gradient-button-blue flex items-center justify-center
    ${
      isPopular
      // ? "bg-cyan-400 hover:bg-cyan-300 !text-black"
      // : "border bg-white/10 hover:bg-white/20 text-white border-white/20"
    }
  `;

  const profitFeature = Array.isArray(features)
    ? features.find(
        (f) =>
          typeof f === "object" &&
          f !== null &&
          (f.key === "تقسیم سود" || f.label === "تقسیم سود"),
      )
    : null;
  const displayFeatures = Array.isArray(features)
    ? [
        ...features.filter(
          (feature) =>
            typeof feature === "string" ||
            ![
              "زمان مرحله ۱",
              "زمان مرحله ۲",
              "کمیسیون و اسپرد معامله",
              "ریسک شناور",
              "تقسیم سود",
            ].includes(feature.key ?? feature.label),
        ),
        { key: "کمیسیون و اسپرد معامله", value: "صفر" },
        { key: "ریسک شناور", value: "ندارد" },
        ...(profitFeature ? [profitFeature] : []),
      ]
    : [];

  return (
    <div className={cardClasses.trim()}>
      {hasDiscount && (
        <div className="absolute -top-4 right-4 px-3 py-1 text-sm font-normal rounded-full bg-zi-blue-light text-zi-text-dark">
          {discountLabel}
        </div>
      )}
      {planName === "Pro" && (
        <div className="absolute -top-4 left-4 px-3 py-1 text-sm font-normal rounded-full bg-zi-blue-light text-zi-text-dark">
          پر طرفدار‌ترین
        </div>
      )}
      <div className="flex-1">
        <div className="mb-0">
          <h2 className="text-[48px] text-center font-extralight tracking-[-0.03em] text-zi-text-light dark:text-zi-text-dark ">
            {planName}
          </h2>
          <div className="w-full flex justify-center items-center">
            <div className="flex flex-col gap-0">
              <p className="text-lg font-medium text-zi-text-light dark:text-zi-text-dark mt-0 font-sans">
                {description}
              </p>
              <span className="text-[14px] text-zi-text-light/70 dark:text-zi-text-dark/70">
                سرمایه اولیه
              </span>
            </div>
          </div>
        </div>

        <div className="w-full flex justify-center items-center my-3 sm:my-5">
          <div className="relative flex items-baseline gap-2">
            <span className="text-[14px] text-zi-text-light/70 dark:text-zi-text-dark/70 absolute bottom-0 -right-2 translate-x-full -translate-y-full"></span>
            {!hasDiscount ? (
              <span className="text-4xl font-extralight text-zi-text-light dark:text-zi-text-dark">
                ${price}
              </span>
            ) : (
              <span className="flex items-center gap-4">
                {/* New discounted price */}
                <span className="text-4xl font-extralight text-zi-text-light dark:text-zi-text-dark">
                  ${discountPrice}
                </span>
                {/* Old price (striked) */}
                <span className="relative text-3xl font-extralight text-zi-text-light/50 dark:text-zi-text-dark/50">
                  ${price}
                  <span className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 w-[120%] h-0.5 bg-zi-border-dark/70 dark:bg-zi-border-dark/95 -rotate-0 rounded-full" />
                </span>
              </span>
            )}
          </div>
        </div>
        {profitFeature ? (
          <div
            className="hidden md:flex justify-center mb-5 items-center w-fit mx-auto gap-2"
            dir="rtl"
          >
            <CheckIcon className="text-zi-border-dark size-3 flex-shrink-0" />
            <div className="flex items-center justify-center w-full gap-8">
              <span className="text-zi-text-light/70 dark:text-zi-text-dark/70 text-sm font-extralight whitespace-nowrap">
                {profitFeature.key ?? profitFeature.label ?? ""}
              </span>
              <span className="text-zi-text-light dark:text-zi-text-dark text-sm font-base tabular-nums whitespace-nowrap">
                {profitFeature.value ?? ""}
              </span>
            </div>
          </div>
        ) : null}
        <RippleButton
          onClick={handlePurchaseClick}
          className={buttonClasses.trim()}
          disabled={isPurchasing}
        >
          {isPurchasing ? (
            <span className="inline-flex w-full items-center justify-center">
              <Loader />
            </span>
          ) : (
            "خرید چالش"
          )}
        </RippleButton>
      </div>

      <div className="card-divider w-full h-px my-3 bg-zi-gradient-divider md:mb-0 md:mx-5 md:w-px md:h-auto md:self-stretch md:shrink-0 md:bg-zi-gradient-divider-vertical" />

      <ul className="flex-[1] flex justify-between flex-col sm:gap-2.5 gap-3 text-[14px] text-zi-text-light/90 dark:text-zi-text-dark/90 mb-3 md:mb-0 font-sans">
        {displayFeatures.map((feature, index) => {
          // Backward compatible: allow string features
          if (typeof feature === "string") {
            return (
              <li key={index} className="flex items-center gap-1.5 sm:gap-2">
                <CheckIcon className="text-zi-border-dark w-4 h-4" />
                <span className="">{feature}</span>
              </li>
            );
          }

          // New format: { key, value } OR { label, value }
          const label = feature.key ?? feature.label ?? "";
          const value = feature.value ?? "";

          return (
            <li
              key={index}
              className={`flex items-center gap-2 w-full ${feature.key === "تقسیم سود" ? "md:hidden" : ""}`}
              dir="rtl"
            >
              <CheckIcon className="text-zi-border-dark size-3 flex-shrink-0" />
              <div className="flex items-center justify-between w-full gap-8">
                {/* label (right side in RTL) */}
                <span className="text-zi-text-light/70 dark:text-zi-text-dark/70 text-sm font-extralight whitespace-nowrap">
                  {label}
                </span>
                {/* value (left side) */}
                <span className="text-zi-text-light dark:text-zi-text-dark text-sm font-base tabular-nums  whitespace-nowrap">
                  {value}
                </span>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export const ChallengeCard = ({ plan }) => {
  return (
    <main className="relative w-full max-w-5xl min-h-fit flex flex-col items-center justify-center mx-auto">
      <div className="w-full">
        <div className="w-full flex justify-center pt-5">
          <div className="w-full">
            <PricingCard {...plan} />
          </div>
        </div>
      </div>
    </main>
  );
};
